import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Device} from "../../../model/device";
import {DeviceFilters} from "../../../model/filters/device-filters";
import * as moment from "moment";
import * as FileSaver from 'file-saver';
import {TimezoneService} from "../../../services/timezone.service";
import {DevicesService} from "../../../services/devices.service";
import {Asset} from "../../../model/asset";
import {Kpi} from "../../../model/kpi";
import {AssetFilters} from "../../../model/filters/asset-filters";
import {KpiFilters} from "../../../model/filters/kpi-filters";
import {AssetsService} from "../../../services/assets.service";
import {KpisService} from "../../../services/kpis.service";
import {ColInfo} from "xlsx";
import {CustomDatePipe} from "../../../shared/pipes/custom-date.pipe";

@Component({
  selector: 'app-export-xls',
  templateUrl: './export-xls.component.html',
  styleUrls: ['./export-xls.component.scss']
})
export class ExportXlsComponent implements OnInit {

  @Input() content: Device[] | Asset[] | Kpi[] | any[];
  @Input() totalContent: number;
  @Input() deviceFilters: DeviceFilters;
  @Input() assetFilters: AssetFilters;
  @Input() kpiFilters: KpiFilters;
  @Input() typeContent: 'DEVICE' | 'ASSET' | 'KPI' | 'MEASUREMENTS';
  @Input() first: number;
  @Input() typeExport: 'XLSX' | 'CSV';
  @Output() visibleChange: EventEmitter<boolean>;
  public dialogVisible: boolean;
  public contentTypeExport: string = 'currentPage';
  public fileName: string;
  public loadingContent: boolean = false;

  constructor(private timezoneService: TimezoneService,
              private devicesService: DevicesService,
              private assetsService: AssetsService,
              private kpisService: KpisService,
              private customDatePipe : CustomDatePipe) {
    this.visibleChange = new EventEmitter<boolean>();
  }

  ngOnInit(): void {

  }

  @Input()
  set visible(visible: boolean) {
    this.dialogVisible = visible;
  }

  exportAllContentToExcel(): void {
    moment.tz(this.timezoneService.currentTimezone);
    import("xlsx").then(xlsx => {
      const contentToExport: any[] = [];
      this.loadingContent = true;
      const promises = [];
      let promise: any;
      if (this.typeContent !== 'MEASUREMENTS') {
        if (this.typeContent === 'DEVICE') {
          promise = this.devicesService.getDevices(0, this.totalContent, this.deviceFilters).toPromise().then(response => {
            response.devices.forEach(device => {
              contentToExport.push({
                serialNumber: device.serialNumber,
                creationAt: moment(device.creationAt).format(),
                description: device.description,
                category: device.deviceCategory ? device.deviceCategory.name : '',
                tenant: device.tenant.name,
                organization: device.organization.name,
                lastValue: moment(device.dateLastValueReported).format()
              });
            });
          });
        } else if (this.typeContent === 'ASSET') {
          promise = this.assetsService.getAssets(0, this.totalContent, this.assetFilters).toPromise().then(response => {
            response.assets.forEach(asset => {
              contentToExport.push({
                serialNumber: asset.alias,
                description: asset.description,
                tenant: asset.tenant.name,
                organization: asset.organization.name,
                representation: asset.assetRepresentation ? asset.assetRepresentation.name: ''
              });
            });
          });
        } else if (this.typeContent === 'KPI') {
          promise = this.kpisService.getKpis(0, this.totalContent, this.kpiFilters).toPromise().then(response => {
            response.kpis.forEach(kpi => {
              contentToExport.push({
                serialNumber: kpi.name,
                description: kpi.description,
                tenant: kpi.tenant.name,
                organization: kpi.organization ? kpi.organization.name : '',
                timePeriod: kpi.timePeriod,
                operation: kpi.operation
              });
            });
          });
        }
        promises.push(promise);
        Promise.all(promises).then(() => {
          this.createSheet(contentToExport, xlsx);
        })
      } else {
        if (this.content != null) {
          this.content.forEach(measure => {
            contentToExport.push({
              Name: measure.measurementName,
              Timestamp: this.customDatePipe.transform(measure.timestamp, { showTimezone: true }),
              Value: measure.value,
              Unit: measure.unit ? measure.unit : ''
            });
          });
        }
        this.createSheet(contentToExport, xlsx);
      }
    });
  }

  exportCurrentContentToExcel(): void {
    import("xlsx").then(xlsx => {
      const contentToExport: any[] = [];
      this.loadingContent = true;
      if (this.typeContent === 'DEVICE') {
        this.content.forEach(device => {
          contentToExport.push({
            serialNumber: device.serialNumber,
            creationAt: moment(device.creationAt).format(),
            description: device.description,
            category: device.deviceCategory ? device.deviceCategory.name : '',
            tenant: device.tenant.name,
            organization: device.organization.name,
            lastValue: moment(device.dateLastValueReported).format()
          });
        });
      } else if (this.typeContent === 'ASSET') {
        this.content.forEach(asset => {
          contentToExport.push({
            serialNumber: asset.alias,
            description: asset.description,
            tenant: asset.tenant.name,
            organization: asset.organization.name,
            representation: asset.assetRepresentation ? asset.assetRepresentation.name: ''
          });
        });
      } else if (this.typeContent === 'KPI') {
        this.content.forEach(kpi => {
          contentToExport.push({
            serialNumber: kpi.name,
            description: kpi.description,
            tenant: kpi.tenant.name,
            organization: kpi.organization ? kpi.organization.name : '',
            timePeriod: kpi.timePeriod,
            operation: kpi.operation
          });
        });
      } else {
        if (this.content != null) {
          this.content.slice(this.first, this.first+10).forEach(measure => {
            contentToExport.push({
              Name: measure.measurementName,
              Timestamp: this.customDatePipe.transform(measure.timestamp, { showTimezone: true }),
              Value: measure.value,
              Unit: measure.unit ? measure.unit : ''
            });
          });
        }
      }
      this.createSheet(contentToExport, xlsx);
    });
  }

  createSheet(content: any[], xlsx: any): void {
    const worksheet = xlsx.utils.json_to_sheet(content);
    if (this.typeExport === 'XLSX') {
      const workbook = {Sheets: {'data': worksheet}, SheetNames: ['data']};
      worksheet['!cols'] = this.fixColsWidth(content);
      const excelBuffer: any = xlsx.write(workbook, {bookType: 'xlsx', type: 'array'});
      this.saveAsExcelFile(excelBuffer, this.fileName);
    } else {
      const csvWorksheet = xlsx.utils.sheet_to_csv(worksheet, {FS:';'});
      this.saveAsCSVFile(csvWorksheet, this.fileName);
    }
    this.loadingContent = false;
  }

  fixColsWidth(content: any[]): ColInfo[] {
    let colsWidth: ColInfo[];
    if (this.typeContent === 'DEVICE') {
      colsWidth = new Array(7);
      colsWidth[0] = {wch: content.reduce((w, r) => Math.max(w, r.serialNumber.length + 2), 12)};
      colsWidth[1] = {wch: content.reduce((w, r) => Math.max(w, r.creationAt.length), 10)};
      colsWidth[2] = {wch: content.reduce((w, r) => Math.max(w, r.description ? r.description.length : 0), 11)};
      colsWidth[3] = {wch: content.reduce((w, r) => Math.max(w, r.category ? r.category.length : 0), 8)};
      colsWidth[4] = {wch: content.reduce((w, r) => Math.max(w, r.tenant.length + 2), 6)};
      colsWidth[5] = {wch: content.reduce((w, r) => Math.max(w, r.organization ? r.organization.length + 2 : 0), 12)};
      colsWidth[6] = {wch: content.reduce((w, r) => Math.max(w, r.lastValue.length), 9)};
    } else if (this.typeContent === 'ASSET') {
      colsWidth = new Array(5);
      colsWidth[0] = {wch: content.reduce((w, r) => Math.max(w, r.serialNumber ? r.serialNumber.length + 2 : 0), 12)};
      colsWidth[1] = {wch: content.reduce((w, r) => Math.max(w, r.description ? r.description.length : 0), 11)};
      colsWidth[2] = {wch: content.reduce((w, r) => Math.max(w, r.tenant.length + 2), 6)};
      colsWidth[3] = {wch: content.reduce((w, r) => Math.max(w, r.organization ? r.organization.length + 2 : 0), 12)};
      colsWidth[4] = {wch: content.reduce((w, r) => Math.max(w, r.representation ? r.representation.length : 0), 14)};
    } else if (this.typeContent === 'KPI') {
      colsWidth = new Array(6);
      colsWidth[0] = {wch: content.reduce((w, r) => Math.max(w, r.serialNumber.length + 6), 12)};
      colsWidth[1] = {wch: content.reduce((w, r) => Math.max(w, r.description ? r.description.length : 0), 11)};
      colsWidth[2] = {wch: content.reduce((w, r) => Math.max(w, r.tenant.length + 2), 6)};
      colsWidth[3] = {wch: content.reduce((w, r) => Math.max(w, r.organization ? r.organization.length + 2 : 0), 12)};
      colsWidth[4] = {wch: content.reduce((w, r) => Math.max(w, r.timePeriod.length + 2), 10)};
      colsWidth[5] = {wch: content.reduce((w, r) => Math.max(w, r.operation.length + 2), 9)};
    } else {
      colsWidth = new Array(3);
      colsWidth[0] = {wch: content.reduce((w, r) => Math.max(w, r.Name.length + 6), 11)};
      colsWidth[1] = {wch: content.reduce((w, r) => Math.max(w, r.Timestamp.length), 9)};
      colsWidth[2] = {wch: content.reduce((w, r) => Math.max(w, r.Value.length), 5)};
      colsWidth[3] = {wch: content.reduce((w, r) => Math.max(w, r.Unit.length), 4)};
    }
    return colsWidth;
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    let EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    let EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE
    });
    FileSaver.saveAs(data, fileName + EXCEL_EXTENSION);
    this.visible = false;
    this.visibleChange.emit(false);
  }

  saveAsCSVFile(buffer: any, fileName: string): void {
    let EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    let EXCEL_EXTENSION = '.csv';
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE
    });
    FileSaver.saveAs(data, fileName + EXCEL_EXTENSION);
    this.visible = false;
    this.visibleChange.emit(false);
  }

  public saveAndClose(): void {
    if (this.contentTypeExport === 'currentPage') {
      this.exportCurrentContentToExcel();
    } else {
      this.exportAllContentToExcel();
    }
  }

  public closeWithoutSaving(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }


}
